import { faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import { differenceInDays, isAfter, isEqual } from "date-fns";
import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExportTnARulesToExcel from "./ExportTnARulesToExcel/ExportTnARulesToExcel";
import { monthsValues } from "../../../containers/CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { CustomDate } from "../../../models/custom-date/custom-date";
import {
  TnAEmployeeConfigDTO,
  TnAEmployeeConfigFilter,
  TnAEmployeeScheduleLinkRef,
} from "../../../models/tna/tna-employee-config";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { renderCellExpand } from "../../../reusable-components/RenderCellExpand/RenderCellExpand";
import Table from "../../../reusable-components/Table/Table";
import { TnAEmployeeConfigApiService } from "../../../services/tna-config/tna-employee-config-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_TNAR } from "../../../utils/constants/filter-tables-constants";
import { VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID } from "../../../utils/constants/tna-constants";
import { convertBooleanToYesOrNo } from "../../../utils/convert-boolean-to-yes-or-no";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import { EmployeeNameContainer } from "./styles";
import { EmployeeRef } from "../../../models/employees";
import { TnACalendarRef } from "../../../models/tna/tna-calendar/tna-calendar-ref";
import { ColumnsHeaderTitle } from "../../../reusable-components/Table/styles";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";

interface State {
  loading: boolean;
  error: string;
}

const TnARules = () => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.tna_employee_config"; // tRP - translation root path
  const tRPTnAFields = "components.tna_config"; // tRP - translation root path for tna fields
  const tRPTnAConfig = "components.tna_config"; // tRPTnAConfig - translation root path TnAConfig
  const tRPCal = "pages.system_settings.tables.tna_calendars";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
  } = useContext(MyTnAContext);

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const navigate = useNavigate();

  const allItems = useRef<TnAEmployeeConfigDTO[]>([]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const [isThreholdsView, setIsThreholdsView] = useState<boolean>(false);

  const clickNavigate = (employee: EmployeeRef | null) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const columns: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      headerName: `${t("common_words.employee")} | ${t("common_words.legal_entity")}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      headerName: t("common_words.tna_employee_group"),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "tnAConfigType", // ???
      // headerName: t(`${tRP}.fields.tna_config_type`), // ???
      renderHeader: () => (
        <Tooltip title={`${t(`${tRP}.fields.tna_config_type`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRP}.fields.tna_config_type`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const config = row.tnAConfig
          ? "Employee"
          : row.employeeGroupTnAConfig
          ? "Group"
          : row.legalEntityTnAConfig
          ? "Legal entity"
          : row.companyAccountTnAConfig
          ? "Company account"
          : row.legislationTnAConfig
          ? "Legislation"
          : row.allLegislationsTnAConfig
          ? "All legislations"
          : "None";

        return (
          <Tooltip
            title={
              <div>
                <div>
                  <Typography>
                    <b>Level:</b> {config}
                  </Typography>
                </div>
                <div>
                  <Typography>
                    <b>Past versions count:</b> {row.pastVersions}
                  </Typography>
                </div>
                <div>
                  <Typography>
                    <b>Future versions count:</b> {row.futureVersions}
                  </Typography>
                </div>
                {!!row.tnAConfig && (
                  <>
                    <div>
                      <Typography>
                        <b>From:</b> {intlFormatDateShort(CustomDate.ToDate(row.fromDate) as Date)}
                      </Typography>
                    </div>
                    <div>
                      <Typography>
                        <b>To:</b> {row?.toDate ? `${intlFormatDateShort(CustomDate.ToDate(row.toDate) as Date)}` : "-"}
                      </Typography>
                    </div>
                  </>
                )}
              </div>
            }
          >
            <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
              <Typography sx={{ width: "100%" }}>
                {`${config} ${row.pastVersions} ${row.parent?.id ? "^" : "_"} ${row.futureVersions}`}
              </Typography>
              {!!row.tnAConfig && (
                <>
                  <Typography sx={{ fontSize: 12 }}>
                    <b>From:</b> {`${intlFormatDateShort(CustomDate.ToDate(row.fromDate) as Date)}`}
                  </Typography>

                  <Typography sx={{ fontSize: 12 }}>
                    <b>To:</b>{" "}
                    {`${row?.toDate ? `${intlFormatDateShort(CustomDate.ToDate(row.toDate) as Date)}` : "-"}`}
                  </Typography>
                </>
              )}
            </Grid>
          </Tooltip>
        );
      },
      flex: 2,
    },
    {
      field: "futureStartDate",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.future_start_date`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.future_start_date`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      renderCell: ({ row }) => {
        const today = new Date();

        const upcomingConfig = row.employeeGroupTnAConfig
          ? "Group"
          : row.legalEntityTnAConfig
          ? "Legal entity"
          : row.companyAccountTnAConfig
          ? "Company account"
          : row.legislationTnAConfig
          ? "Legislation"
          : row.allLegislationsTnAConfig
          ? "All legislations"
          : "None";

        const hasGapBetweenEmployeesLevel =
          !!row?.pastVersions &&
          !!row?.toDate &&
          ((!!row?.nextFromDate &&
            !isEqual(CustomDate.ToDate(row.toDate) as Date, CustomDate.ToDate(row.nextFromDate) as Date)) ||
            (!row?.nextFromDate &&
              !isAfter(
                new Date(today.getFullYear(), today.getMonth(), today.getDay()),
                CustomDate.ToDate(row.toDate) as Date
              )));

        const hasUpcomingConfig =
          !!row?.futureVersions ||
          (!row?.futureVersions &&
            !!row?.toDate &&
            !isAfter(
              new Date(today.getFullYear(), today.getMonth(), today.getDay()),
              CustomDate.ToDate(row.toDate) as Date
            ));

        const nextEmployeeLevelStartsIn: number | null = row?.futureVersions
          ? differenceInDays(CustomDate.ToDate(row.nextFromDate) as Date, today)
          : null;

        const nextOtherLevelStartsIn: number | null = hasGapBetweenEmployeesLevel
          ? differenceInDays(CustomDate.ToDate(row.toDate) as Date, today)
          : null;

        return (
          <>
            {hasUpcomingConfig && (
              <Tooltip
                title={
                  <div>
                    {hasGapBetweenEmployeesLevel && (
                      <Typography>
                        {`${t("common_words.days.days", {
                          count: nextOtherLevelStartsIn ?? 0,
                        })} (${upcomingConfig} level)`}
                      </Typography>
                    )}

                    {!!row.nextFromDate && (
                      <Typography>
                        {`${t("common_words.days.days", {
                          count: nextEmployeeLevelStartsIn ?? 0,
                        })} (Employee level)`}
                      </Typography>
                    )}
                  </div>
                }
              >
                <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                  {hasGapBetweenEmployeesLevel && (
                    <Typography sx={{ width: "100%" }}>
                      {`${t("common_words.days.days", {
                        count: nextOtherLevelStartsIn ?? 0,
                      })} (${upcomingConfig} level)`}
                    </Typography>
                  )}
                  {!!row.nextFromDate && (
                    <Typography sx={{ width: "100%", fontSize: 12 }}>
                      {`${t("common_words.days.days", {
                        count: nextEmployeeLevelStartsIn ?? 0,
                      })} (Employee level)`}
                    </Typography>
                  )}
                </Grid>
              </Tooltip>
            )}
          </>
        );
      },
      valueGetter: ({ row }) =>
        row?.nextFromDate ? `${intlFormatDateShort(CustomDate.ToDate(row.nextFromDate) as Date)}` : "",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "how2ReportTime",
      // headerName: t(`${tRPTnAFields}.how_2_report_time`),
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAFields}.how_2_report_time`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAFields}.how_2_report_time`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? row.actualConfig?.how2ReportTime?.name
          : row?.tnAConfig
          ? row.tnAConfig?.how2ReportTime?.name
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.how2ReportTime?.name
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.how2ReportTime?.name
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.how2ReportTime?.name
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.how2ReportTime?.name
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.how2ReportTime?.name
          : "",
      headerAlign: "center",
      renderCell: renderCellExpand,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "scheduleType",
      // headerName: t(`${tRPTnAFields}.how_2_report_time`),
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAFields}.schedule_type`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAFields}.schedule_type`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? row.actualConfig?.scheduleType?.name
          : row?.tnAConfig
          ? row.tnAConfig?.scheduleType?.name
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.scheduleType?.name
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.scheduleType?.name
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.scheduleType?.name
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.scheduleType?.name
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.scheduleType?.name
          : "",
      headerAlign: "center",
      renderCell: renderCellExpand,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "schedule",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.schedule`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.schedule`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row.actualConfig?.schedules?.length > 1
          ? `w1: ${
              row.actualConfig?.schedules.find((sch: TnAEmployeeScheduleLinkRef) => sch.weekNumber === 1).schedule.code
            },\n w2: ${
              row.actualConfig?.schedules.find((sch: TnAEmployeeScheduleLinkRef) => sch.weekNumber === 2).schedule.code
            },\n w3: ${
              row.actualConfig?.schedules.find((sch: TnAEmployeeScheduleLinkRef) => sch.weekNumber === 3).schedule.code
            },\n w4: ${
              row.actualConfig?.schedules.find((sch: TnAEmployeeScheduleLinkRef) => sch.weekNumber === 4).schedule.code
            }`
          : row.actualConfig?.schedules?.length === 1
          ? row.actualConfig.schedules[0]?.schedule?.name
          : row.actualConfig?.schedule
          ? row.actualConfig?.schedule?.name
          : "",
      headerAlign: "center",
      renderCell: renderCellExpand,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "employeeCanClaimExtraTime",
      // headerName: t(`${tRPTnAConfig}.employee_can_claim_extra_time`),
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.employee_can_claim_extra_time`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.employee_can_claim_extra_time`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? convertBooleanToYesOrNo(row.actualConfig["employeeCanClaimExtraTime"])
          : row?.tnAConfig
          ? convertBooleanToYesOrNo(row.tnAConfig["employeeCanClaimExtraTime"])
          : row?.employeeGroupTnAConfig
          ? convertBooleanToYesOrNo(row.employeeGroupTnAConfig["employeeCanClaimExtraTime"])
          : row?.legalEntityTnAConfig
          ? convertBooleanToYesOrNo(row.legalEntityTnAConfig["employeeCanClaimExtraTime"])
          : row?.companyAccountTnAConfig
          ? convertBooleanToYesOrNo(row.companyAccountTnAConfig["employeeCanClaimExtraTime"])
          : row?.legislationTnAConfig
          ? convertBooleanToYesOrNo(row.legislationTnAConfig["employeeCanClaimExtraTime"])
          : row?.allLegislationsTnAConfig
          ? convertBooleanToYesOrNo(row.allLegislationsTnAConfig["employeeCanClaimExtraTime"])
          : "",
      flex: 1,
      align: "center",
    },
    {
      field: "billableTimeType",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.employee_can_report_billable_time`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.employee_can_report_billable_time`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.tnAConfig
          ? row.tnAConfig["billableTimeType"]?.name ?? ""
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig["billableTimeType"]?.name ?? ""
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig["billableTimeType"]?.name ?? ""
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig["billableTimeType"]?.name ?? ""
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig["billableTimeType"]?.name ?? ""
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig["billableTimeType"]?.name ?? ""
          : "",
      headerAlign: "center",
      renderCell: renderCellExpand,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "publicHolidaysCalendars",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.public_holidays_calendars`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.public_holidays_calendars`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.tnAConfig
          ? row.tnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.publicHolidaysCalendars?.map((c: TnACalendarRef) => c.name).join(", ")
          : "",
      headerAlign: "center",
      renderCell: renderCellExpand,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
    {
      field: "vacationDaysAnnualAllowance",
      // headerName: t(`${tRPTnAFields}.vacation_days_annual_allowance`),
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAFields}.vacation_days_annual_allowance`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAFields}.vacation_days_annual_allowance`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        Number(
          row.actualConfig?.vacationDaysAnnualAllowance ??
            row.tnAConfig?.vacationDaysAnnualAllowance ??
            row.employeeGroupTnAConfig?.vacationDaysAnnualAllowance ??
            row.legalEntityTnAConfig?.vacationDaysAnnualAllowance ??
            row.companyAccountTnAConfig?.vacationDaysAnnualAllowance ??
            row.legislationTnAConfig?.vacationDaysAnnualAllowance ??
            row.allLegislationsTnAConfig?.vacationDaysAnnualAllowance ??
            0
        ).toFixed(2),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      align: "right",
    },
    {
      field: "vacationIncludesWeekends",
      // headerName: t(`${tRPTnAConfig}.employee_can_claim_extra_time`),
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.vacation_includes_weekends`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.vacation_includes_weekends`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? convertBooleanToYesOrNo(row.actualConfig["vacationIncludesWeekends"])
          : row?.tnAConfig
          ? convertBooleanToYesOrNo(row.tnAConfig["vacationIncludesWeekends"])
          : row?.employeeGroupTnAConfig
          ? convertBooleanToYesOrNo(row.employeeGroupTnAConfig["vacationIncludesWeekends"])
          : row?.legalEntityTnAConfig
          ? convertBooleanToYesOrNo(row.legalEntityTnAConfig["vacationIncludesWeekends"])
          : row?.companyAccountTnAConfig
          ? convertBooleanToYesOrNo(row.companyAccountTnAConfig["vacationIncludesWeekends"])
          : row?.legislationTnAConfig
          ? convertBooleanToYesOrNo(row.legislationTnAConfig["vacationIncludesWeekends"])
          : row?.allLegislationsTnAConfig
          ? convertBooleanToYesOrNo(row.allLegislationsTnAConfig["vacationIncludesWeekends"])
          : "",
      flex: 1,
      align: "center",
    },
    {
      field: "vacationExcludesOffScheduleWorkDays",
      // headerName: t(`${tRPTnAConfig}.employee_can_claim_extra_time`),
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.vacation_excludes_offSchedule`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.vacation_excludes_offSchedule`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? convertBooleanToYesOrNo(row.actualConfig["vacationExcludesOffScheduleWorkDays"])
          : row?.tnAConfig
          ? convertBooleanToYesOrNo(row.tnAConfig["vacationExcludesOffScheduleWorkDays"])
          : row?.employeeGroupTnAConfig
          ? convertBooleanToYesOrNo(row.employeeGroupTnAConfig["vacationExcludesOffScheduleWorkDays"])
          : row?.legalEntityTnAConfig
          ? convertBooleanToYesOrNo(row.legalEntityTnAConfig["vacationExcludesOffScheduleWorkDays"])
          : row?.companyAccountTnAConfig
          ? convertBooleanToYesOrNo(row.companyAccountTnAConfig["vacationExcludesOffScheduleWorkDays"])
          : row?.legislationTnAConfig
          ? convertBooleanToYesOrNo(row.legislationTnAConfig["vacationExcludesOffScheduleWorkDays"])
          : row?.allLegislationsTnAConfig
          ? convertBooleanToYesOrNo(row.allLegislationsTnAConfig["vacationExcludesOffScheduleWorkDays"])
          : "",
      flex: 1,
      align: "center",
    },
    {
      field: "vacationDaysAddedAt",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.vacation_days_added_at`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.vacation_days_added_at`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row.tnAConfig
          ? (row.tnAConfig?.vacationDaysAddedAt?.name ?? "") +
            (row.tnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
              ? `: ${t(
                  `${tRPCal}.month_${
                    row.tnAConfig?.vacationDaysAddedOn + 1 < 10
                      ? `0${row.tnAConfig?.vacationDaysAddedOn + 1}`
                      : row.tnAConfig?.vacationDaysAddedOn + 1
                  }`
                )}`
              : " ")
          : row.employeeGroupTnAConfig
          ? (row.employeeGroupTnAConfig?.vacationDaysAddedAt?.name ?? "") +
            (row.employeeGroupTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
              ? `: ${t(
                  `${tRPCal}.month_${
                    row.employeeGroupTnAConfig?.vacationDaysAddedOn + 1 < 10
                      ? `0${row.employeeGroupTnAConfig?.vacationDaysAddedOn + 1}`
                      : row.employeeGroupTnAConfig?.vacationDaysAddedOn + 1
                  }`
                )}`
              : " ")
          : row.legalEntityTnAConfig
          ? (row.legalEntityTnAConfig?.vacationDaysAddedAt?.name ?? "") +
            (row.legalEntityTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
              ? `: ${t(
                  `${tRPCal}.month_${
                    row.legalEntityTnAConfig?.vacationDaysAddedOn + 1 < 10
                      ? `0${row.legalEntityTnAConfig?.vacationDaysAddedOn + 1}`
                      : row.legalEntityTnAConfig?.vacationDaysAddedOn + 1
                  }`
                )}`
              : " ")
          : row.companyAccountTnAConfig
          ? (row.companyAccountTnAConfig?.vacationDaysAddedAt?.name ?? "") +
            (row.companyAccountTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
              ? `: ${t(
                  `${tRPCal}.month_${
                    row.companyAccountTnAConfig?.vacationDaysAddedOn + 1 < 10
                      ? `0${row.companyAccountTnAConfig?.vacationDaysAddedOn + 1}`
                      : row.companyAccountTnAConfig?.vacationDaysAddedOn + 1
                  }`
                )}`
              : " ")
          : row.legislationTnAConfig
          ? (row.legislationTnAConfig?.vacationDaysAddedAt?.name ?? "") +
            (row.legislationTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
              ? `: ${t(
                  `${tRPCal}.month_${
                    row.legislationTnAConfig?.vacationDaysAddedOn + 1 < 10
                      ? `0${row.legislationTnAConfig?.vacationDaysAddedOn + 1}`
                      : row.legislationTnAConfig?.vacationDaysAddedOn + 1
                  }`
                )}`
              : " ")
          : row.allLegislationsTnAConfig
          ? (row.allLegislationsTnAConfig?.vacationDaysAddedAt?.name ?? "") +
            (row.allLegislationsTnAConfig?.vacationDaysAddedAt?.id === VACATION_ADDED_ON_BEGINNING_OF_THE_YEAR_ID
              ? `: ${t(
                  `${tRPCal}.month_${
                    row.allLegislationsTnAConfig?.vacationDaysAddedOn + 1 < 10
                      ? `0${row.allLegislationsTnAConfig?.vacationDaysAddedOn + 1}`
                      : row.allLegislationsTnAConfig?.vacationDaysAddedOn + 1
                  }`
                )}`
              : " ")
          : "",
      headerAlign: "center",
      renderCell: renderCellExpand,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
    },
  ];

  const columnsForThreholdsView: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      headerName: `${t("common_words.employee")} | ${t("common_words.legal_entity")}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      headerName: t("common_words.tna_employee_group"),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "workedTimeMoreThan",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.highlight_when_worked_time_more`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.highlight_when_worked_time_more`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? row.actualConfig?.workedTimeMoreThan
          : row?.tnAConfig
          ? row.tnAConfig?.workedTimeMoreThan
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.workedTimeMoreThan
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.workedTimeMoreThan
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.workedTimeMoreThan
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.workedTimeMoreThan
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.workedTimeMoreThan
          : "",
      align: "center",
      sortable: true,
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "vacationBalanceMoreThan",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.highlight_when_vacation_balance_more`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.highlight_when_vacation_balance_more`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? row.actualConfig?.vacationBalanceMoreThan
          : row?.tnAConfig
          ? row.tnAConfig?.vacationBalanceMoreThan
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.vacationBalanceMoreThan
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.vacationBalanceMoreThan
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.vacationBalanceMoreThan
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.vacationBalanceMoreThan
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.vacationBalanceMoreThan
          : "",
      align: "center",
      sortable: true,
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "vacationBalanceAfterDay,vacationBalanceAfterMonth",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.vacation_balance_after_date`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.vacation_balance_after_date`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        !!row?.actualConfig &&
        Number.isInteger(row?.actualConfig?.vacationBalanceAfterDay) &&
        row?.actualConfig?.vacationBalanceAfterDay !== 0 &&
        Number.isInteger(row?.actualConfig?.vacationBalanceAfterMonth)
          ? `${row.actualConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.actualConfig.vacationBalanceAfterMonth)?.key ?? ""
            )}`
          : !!row?.tnAConfig &&
            Number.isInteger(row?.tnAConfig?.vacationBalanceAfterDay) &&
            row?.tnAConfig?.vacationBalanceAfterDay !== 0 &&
            Number.isInteger(row?.tnAConfig?.vacationBalanceAfterMonth)
          ? `${row.tnAConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.tnAConfig.vacationBalanceAfterMonth)?.key ?? ""
            )}`
          : !!row?.employeeGroupTnAConfig &&
            Number.isInteger(row?.employeeGroupTnAConfig?.vacationBalanceAfterDay) &&
            row?.employeeGroupTnAConfig?.vacationBalanceAfterDay !== 0 &&
            Number.isInteger(row?.employeeGroupTnAConfig?.vacationBalanceAfterMonth)
          ? `${row.employeeGroupTnAConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.employeeGroupTnAConfig.vacationBalanceAfterMonth)?.key ??
                ""
            )}`
          : !!row?.legalEntityTnAConfig &&
            Number.isInteger(row?.legalEntityTnAConfig?.vacationBalanceAfterDay) &&
            row?.legalEntityTnAConfig?.vacationBalanceAfterDay !== 0 &&
            Number.isInteger(row?.legalEntityTnAConfig?.vacationBalanceAfterMonth)
          ? `${row.legalEntityTnAConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.legalEntityTnAConfig.vacationBalanceAfterMonth)?.key ??
                ""
            )}`
          : !!row?.companyAccountTnAConfig &&
            Number.isInteger(row?.companyAccountTnAConfig?.vacationBalanceAfterDay) &&
            row?.companyAccountTnAConfig?.vacationBalanceAfterDay !== 0 &&
            Number.isInteger(row?.companyAccountTnAConfig?.vacationBalanceAfterMonth)
          ? `${row.companyAccountTnAConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.companyAccountTnAConfig.vacationBalanceAfterMonth)
                ?.key ?? ""
            )}`
          : !!row?.legislationTnAConfig &&
            Number.isInteger(row?.legislationTnAConfig?.vacationBalanceAfterDay) &&
            row?.legislationTnAConfig?.vacationBalanceAfterDay !== 0 &&
            Number.isInteger(row?.legislationTnAConfig?.vacationBalanceAfterMonth)
          ? `${row.legislationTnAConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.legislationTnAConfig.vacationBalanceAfterMonth)?.key ??
                ""
            )}`
          : !!row?.allLegislationsTnAConfig &&
            Number.isInteger(row?.allLegislationsTnAConfig?.vacationBalanceAfterDay) &&
            row?.allLegislationsTnAConfig?.vacationBalanceAfterDay !== 0 &&
            Number.isInteger(row?.allLegislationsTnAConfig?.vacationBalanceAfterMonth)
          ? `${row.allLegislationsTnAConfig.vacationBalanceAfterDay} ${t(
              monthsValues.find((month) => month.value === row.allLegislationsTnAConfig.vacationBalanceAfterMonth)
                ?.key ?? ""
            )}`
          : "",
      align: "center",
      sortable: true,
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "delayInSignOffMoreThan",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_more`)}`}>
          <ColumnsHeaderTitle>{`${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_more`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? row.actualConfig?.delayInSignOffMoreThan
          : row?.tnAConfig
          ? row.tnAConfig?.delayInSignOffMoreThan
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.delayInSignOffMoreThan
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.delayInSignOffMoreThan
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.delayInSignOffMoreThan
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.delayInSignOffMoreThan
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.delayInSignOffMoreThan
          : "",
      align: "center",
      sortable: true,
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "delayInSendingSignOffMoreThan",
      renderHeader: () => (
        <Tooltip title={`${t(`${tRPTnAConfig}.highlight_when_delay_in_signoff_reminders_more`)}`}>
          <ColumnsHeaderTitle>{`${t(
            `${tRPTnAConfig}.highlight_when_delay_in_signoff_reminders_more`
          )}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      valueGetter: ({ row }) =>
        row?.actualConfig
          ? row.actualConfig?.delayInSendingSignOffMoreThan
          : row?.tnAConfig
          ? row.tnAConfig?.delayInSendingSignOffMoreThan
          : row?.employeeGroupTnAConfig
          ? row.employeeGroupTnAConfig?.delayInSendingSignOffMoreThan
          : row?.legalEntityTnAConfig
          ? row.legalEntityTnAConfig?.delayInSendingSignOffMoreThan
          : row?.companyAccountTnAConfig
          ? row.companyAccountTnAConfig?.delayInSendingSignOffMoreThan
          : row?.legislationTnAConfig
          ? row.legislationTnAConfig?.delayInSendingSignOffMoreThan
          : row?.allLegislationsTnAConfig
          ? row.allLegislationsTnAConfig?.delayInSendingSignOffMoreThan
          : "",
      align: "center",
      sortable: true,
      headerAlign: "center",
      flex: 1,
    },
  ];

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnARulesData = useCallback(
    async (pageSize = 10) => {
      try {
        const response = await new TnAEmployeeConfigApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
          } as TnAEmployeeConfigFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
    ]
  );

  return (
    <LoadingOverlay
      loading={state.loading}
      error={state.error}
    >
      {(hasAccess || allowedToViewOnly) && (
        <>
          {!isThreholdsView && (
            <Table
              translationRootPath={tRP}
              columns={columns}
              serviceApi={new TnAEmployeeConfigApiService()}
              filter={FT_TNAR}
              addEditType="page"
              addEditPageRootPath="tna-config"
              addEditPageBaseProp={"employee"}
              propsFilter={
                {
                  legalEntityIds: legalEntityOption?.legalEntityIds,
                  employeeGroupIds: employeeGroupOption?.employeeGroupIds,
                  onlyCurrentEmployees: onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
                } as TnAEmployeeConfigFilter
              }
              quickFilters={
                <QuickfiltersContainer>
                  <>
                    {!!legalEntityQuickFilter && legalEntityQuickFilter}
                    {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
                    {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
                    <Button
                      variant="contained"
                      onClick={() => setIsThreholdsView(true)}
                    >
                      {`${t(`${tRPTnAConfig}.buttons.see_thresholds_settings`)}`}
                    </Button>
                    <ExportTnARulesToExcel
                      loadEmployeeGroup={loadEmployeeGroup}
                      loadTnARulesData={loadTnARulesData}
                      disabled={state.loading}
                      allItems={() => allItems.current}
                    />
                  </>
                </QuickfiltersContainer>
              }
              readOnly
              getAllItems={(items) => {
                allItems.current = items as TnAEmployeeConfigDTO[];
              }}
            />
          )}

          {isThreholdsView && (
            <Table
              translationRootPath={tRP}
              columns={columnsForThreholdsView}
              serviceApi={new TnAEmployeeConfigApiService()}
              filter={FT_TNAR}
              addEditType="page"
              addEditPageRootPath="tna-config"
              addEditPageBaseProp={"employee"}
              propsFilter={
                {
                  legalEntityIds: legalEntityOption?.legalEntityIds,
                  employeeGroupIds: employeeGroupOption?.employeeGroupIds,
                  onlyCurrentEmployees: onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
                } as TnAEmployeeConfigFilter
              }
              quickFilters={
                <QuickfiltersContainer>
                  <>
                    {!!legalEntityQuickFilter && legalEntityQuickFilter}
                    {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
                    {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
                    <Button
                      variant="contained"
                      onClick={() => setIsThreholdsView(false)}
                    >
                      {`${t(`${tRPTnAConfig}.buttons.main_view`)}`}
                    </Button>
                    <ExportTnARulesToExcel
                      loadEmployeeGroup={loadEmployeeGroup}
                      loadTnARulesData={loadTnARulesData}
                      disabled={state.loading}
                      allItems={() => allItems.current}
                    />
                  </>
                </QuickfiltersContainer>
              }
              readOnly
              getAllItems={(items) => {
                allItems.current = items as TnAEmployeeConfigDTO[];
              }}
            />
          )}
        </>
      )}
    </LoadingOverlay>
  );
};

export default TnARules;
